import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  PrimaryButton,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { ExpansionPanel } from '@bb-ui/react-library/dist/components/ExpansionPanel';
import { ExpansionPanelSummary } from '@bb-ui/react-library/dist/components/ExpansionPanelSummary';
import { ExpansionPanelDetails } from '@bb-ui/react-library/dist/components/ExpansionPanelDetails';
import { useTranslation } from 'react-i18next';
import { SiteResponse } from '../hooks/useSiteFiltering';

interface SearchResultsSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  searchedCount: number;
  validCandidates: SiteResponse[];
  sitesInCohort: Array<{
    captain_id: string;
    cohort: string;
    cohort_name: string;
    learn_url?: string;
  }>;
  sitesNotFound: string[];
  sitesNotCandidate: Array<{ captain_id: string; reason: string; learn_url?: string }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '700px',
    maxWidth: '900px',
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  summaryContainer: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  summaryText: {
    fontWeight: 500,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryFirstLine: {
    marginBottom: theme.spacing(1.5),
  },
  validCandidatesText: {
    color: theme.palette.success.main,
  },
  notCandidatesText: {
    color: theme.palette.warning.main,
  },
  sectionContainer: {
    marginBottom: theme.spacing(2.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  expansionPanel: {
    border: `1px solid ${theme.palette.divider}`,
    '&:before': {
      display: 'none',
    },
  },
  expansionPanelSummary: {
    padding: theme.spacing(2, 3),
    minHeight: '56px',
    '&.Mui-expanded': {
      minHeight: '56px',
    },
  },
  expansionPanelDetails: {
    padding: theme.spacing(2, 3, 3, 3),
  },
  panelTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sitesList: {
    maxHeight: '200px',
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    padding: theme.spacing(0.5, 0),
  },
  siteItem: {
    padding: theme.spacing(0.75, 0),
    lineHeight: 1.6,
  },
  subsectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontSize: '0.9375rem',
    '&:first-child': {
      marginTop: 0,
    },
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
}));

const SearchResultsSummaryDialog: React.FC<SearchResultsSummaryDialogProps> = ({
  open,
  onClose,
  searchedCount,
  validCandidates,
  sitesInCohort,
  sitesNotFound,
  sitesNotCandidate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [expandedValidCandidates, setExpandedValidCandidates] = React.useState(true);
  const [expandedNotCandidates, setExpandedNotCandidates] = React.useState(false);

  const notCandidatesCount = sitesInCohort.length + sitesNotFound.length + sitesNotCandidate.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
        {t('cohorts.bulkSearch.results.title')}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.summaryContainer}>
          <Typography className={`${classes.summaryText} ${classes.summaryFirstLine}`}>
            {t('cohorts.bulkSearch.results.summary', { count: searchedCount })}
          </Typography>
          <Typography className={`${classes.summaryText} ${classes.validCandidatesText}`}>
            {t('cohorts.bulkSearch.results.validCandidates', { count: validCandidates.length })}
          </Typography>
          <Typography className={`${classes.summaryText} ${classes.notCandidatesText}`}>
            {t('cohorts.bulkSearch.results.notCandidates', { count: notCandidatesCount })}
          </Typography>
        </div>

        {validCandidates.length > 0 && (
          <div className={classes.sectionContainer}>
            <ExpansionPanel
              className={classes.expansionPanel}
              expanded={expandedValidCandidates}
              onChange={(_, isExpanded) => setExpandedValidCandidates(isExpanded)}
            >
              <ExpansionPanelSummary
                id="valid-candidates-summary"
                aria-controls="valid-candidates-details"
                className={classes.expansionPanelSummary}
              >
                <div className={classes.panelTitle}>
                  <Typography variant="h6">
                    {t('cohorts.bulkSearch.results.validCandidatesSection.title', {
                      count: validCandidates.length,
                    })}
                  </Typography>
                </div>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails className={classes.expansionPanelDetails}>
                <div style={{ width: '100%' }}>
                  <Typography variant="body2" className={classes.description}>
                    {t('cohorts.bulkSearch.results.validCandidatesSection.description')}
                  </Typography>
                  <div className={classes.sitesList}>
                    {validCandidates.map((site) => (
                      <div key={site.captain_id} className={classes.siteItem}>
                        <Typography variant="body2">
                          {t('cohorts.bulkSearch.results.validCandidatesSection.siteItem', {
                            captainId: site.captain_id,
                            learnUrl: site.learn_url || '-',
                          })}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </div>
        )}

        {notCandidatesCount > 0 && (
          <div className={classes.sectionContainer}>
            <ExpansionPanel
              className={classes.expansionPanel}
              expanded={expandedNotCandidates}
              onChange={(_, isExpanded) => setExpandedNotCandidates(isExpanded)}
            >
              <ExpansionPanelSummary
                id="not-candidates-summary"
                aria-controls="not-candidates-details"
                className={classes.expansionPanelSummary}
              >
                <div className={classes.panelTitle}>
                  <Typography variant="h6">
                    {t('cohorts.bulkSearch.results.notCandidatesSection.title', {
                      count: notCandidatesCount,
                    })}
                  </Typography>
                </div>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails className={classes.expansionPanelDetails}>
                <div style={{ width: '100%' }}>
                  {sitesInCohort.length > 0 && (
                    <>
                      <Typography variant="subtitle1" className={classes.subsectionTitle}>
                        {t('cohorts.bulkSearch.results.notCandidatesSection.inCohort.title', {
                          count: sitesInCohort.length,
                        })}
                      </Typography>
                      <div className={classes.sitesList}>
                        {sitesInCohort.map((site) => (
                          <div key={site.captain_id} className={classes.siteItem}>
                            <Typography variant="body2">
                              {t(
                                'cohorts.bulkSearch.results.notCandidatesSection.inCohort.siteItem',
                                {
                                  captainId: site.captain_id,
                                  cohortName: site.cohort_name,
                                  cohortId: site.cohort,
                                },
                              )}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {sitesNotFound.length > 0 && (
                    <>
                      <Typography variant="subtitle1" className={classes.subsectionTitle}>
                        {t('cohorts.bulkSearch.results.notCandidatesSection.notFound.title', {
                          count: sitesNotFound.length,
                        })}
                      </Typography>
                      <div className={classes.sitesList}>
                        {sitesNotFound.map((captainId) => (
                          <div key={captainId} className={classes.siteItem}>
                            <Typography variant="body2">
                              {t(
                                'cohorts.bulkSearch.results.notCandidatesSection.notFound.siteItem',
                                {
                                  captainId,
                                  description: t(
                                    'cohorts.bulkSearch.results.notCandidatesSection.notFound.description',
                                  ),
                                },
                              )}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {sitesNotCandidate.length > 0 && (
                    <>
                      <Typography variant="subtitle1" className={classes.subsectionTitle}>
                        {t('cohorts.bulkSearch.results.notCandidatesSection.otherReasons.title', {
                          count: sitesNotCandidate.length,
                        })}
                      </Typography>
                      <div className={classes.sitesList}>
                        {sitesNotCandidate.map((site) => (
                          <div key={site.captain_id} className={classes.siteItem}>
                            <Typography variant="body2">
                              {t(
                                'cohorts.bulkSearch.results.notCandidatesSection.otherReasons.siteItem',
                                {
                                  captainId: site.captain_id,
                                  reason: site.reason,
                                },
                              )}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <PrimaryButton onClick={onClose}>{t('cohorts.bulkSearch.results.close')}</PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SearchResultsSummaryDialog;
